// Alpine controller for the site-level "property-cards" CMS widget.
//
// The widget is a reorderable LIST of property cards (one per published
// property). Each row opens a per-card modal (the canonical CMS-modal shell:
// sticky header/footer + scrolling body) that edits the card's teaser, a
// featured-amenities repeater ({icon,label}, max 3 — reusing the shared
// @IconTriggerButton + text-input row), a fixed 7-value personas multi-select,
// and the card's images via the REUSED image picker (cmsImagePicker, embedded
// per-card under the `editing` x-if block).
//
// THREE emit channels (all via the shared cms-image-changed bus the cmsEditor
// controller routes):
//   • PER-CARD (transactional): the modal edits a working card object; Done
//     (confirmCard) emits {teaser, featured_amenities, personas} — NOT image_ids
//     — to property:card:<slug>. Cancel restores the pre-edit snapshot with no
//     event. Mirrors review-picker / amenity-grouping's openModal→confirm/discard.
//   • IMAGES (autosave): the embedded cmsImagePicker owns the card's images; its
//     own Save emits {field:'image_ids', target:'property:card-images:<slug>',…}
//     which editor.ts deep-merges into the same card object. So Done excludes
//     image_ids (the picker already committed them — like images everywhere else).
//   • ORDER (immediate): a SortableJS reorder of the card list emits the slug
//     array to site:property_order on every drop (no modal involved).
//
// Mirrors static/js/cms/review-picker.ts + amenity-grouping.ts for the config-
// parse shape, the Sortable init-in-$nextTick pattern, applySortableReorder's
// (el, evt, data, rowSelector) signature, and the $root cms-image-changed dispatch.

import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";
import { parseJsonOr } from "../shared/utils";

// One property card. slug + name are immutable identity/display (name shown on
// the rail row + modal header); the rest is editable in the modal.
interface CardSeed {
  slug: string;
  name: string;
  teaser: string;
  image_ids: string[];
  featured_amenities: Array<{ icon?: string; label: string }>;
  personas: string[];
  // Hidden mirrors WebsiteProperty.Hidden (Task 8): the accommodation's page
  // is hidden site-wide. Read-only here — a dimmed row + badge indicator;
  // visibility toggles exclusively in the sidebar (Task 14).
  hidden: boolean;
}

interface CMSPropertyCardsConfig {
  field: string;
  cards: string; // JSON of CardSeed[]
  order: string; // JSON of string[] (currently informational; the cards array IS the order)
  pools: string; // JSON of Record<slug, Array<{id,url}>>
  // Editor base URL (`/organizations/{orgSlug}/websites/{websiteID}`). Threaded so
  // the embedded per-card image picker (a separate cmsImagePicker instantiated in
  // the templ, scoped under `editing`) can reach the draft upload endpoints. The
  // templ references `baseURL` (this returned-state field) from the picker's x-data.
  baseURL: string;
}

const MAX_AMENITIES = 3;

export function cmsPropertyCards(config: CMSPropertyCardsConfig) {
  const cards = parseJsonOr(config.cards, [] as CardSeed[]);
  // Normalize: guard against null arrays so .includes/.filter/.map never throw.
  for (const c of cards) {
    c.image_ids = Array.isArray(c.image_ids) ? c.image_ids : [];
    c.featured_amenities = Array.isArray(c.featured_amenities) ? c.featured_amenities : [];
    c.personas = Array.isArray(c.personas) ? c.personas : [];
  }
  const pools = parseJsonOr(config.pools, {} as Record<string, Array<{ id: string; url: string }>>);

  return {
    field: config.field,
    cards,
    pools,
    // Editor base URL, referenced by the embedded image picker's x-data in the templ.
    baseURL: config.baseURL,
    // The card currently open in the modal (a live reference into `cards`), or
    // null when the modal is closed.
    editing: null as CardSeed | null,
    // Deep-copy snapshot captured on openCard(), restored on cancelCard().
    _preEdit: null as CardSeed | null,
    // Live Sortable instance so destroy() can tear it down.
    _sortable: null as Sortable | null,
    // Highlights-repeater Sortable (lives on the per-open x-ref="hlList"); created
    // in openCard's $nextTick, destroyed on close (the modal content is rebuilt by
    // x-if="editing", so the element is fresh each open).
    _hlSortable: null as Sortable | null,

    // SortableJS lives on the stable x-ref="cardList"; the keyed x-for only swaps
    // the row children, so the instance survives. Wire in $nextTick so the rows
    // are rendered. If the list isn't present (no cards), the helper no-ops.
    init() {
      (this as any).$nextTick(() => this._initSortable());
    },
    destroy() {
      this._sortable?.destroy();
      this._sortable = null;
      this._teardownHlSortable();
    },

    // The card object for a slug (live reference).
    cardBySlug(slug: string): CardSeed | undefined {
      return this.cards.find((c) => c.slug === slug);
    },

    // The image pool for a property (id+url pairs), empty when the slug has none.
    poolFor(slug: string): Array<{ id: string; url: string }> {
      return this.pools[slug] || [];
    },

    // --- per-card editing (transactional) ---

    // Snapshot a deep copy for clean cancel, then open the modal on the live card.
    openCard(slug: string) {
      const c = this.cardBySlug(slug);
      if (!c) return;
      this._preEdit = JSON.parse(JSON.stringify(c)) as CardSeed;
      this.editing = c;
      const dlg = (this as any).$refs.cardDlg as HTMLDialogElement | undefined;
      dlg?.showModal();
      // The modal body (incl. x-ref="hlList") is rendered by x-if once editing is
      // set; wire the highlights Sortable after that flush.
      (this as any).$nextTick(() => this._initHlSortable());
    },

    // Restore the pre-edit copy in place (no event fired) and close.
    cancelCard() {
      this._teardownHlSortable();
      if (this._preEdit && this.editing) {
        const i = this.cards.findIndex((c) => c.slug === this.editing!.slug);
        if (i !== -1) this.cards[i] = this._preEdit;
      }
      this._preEdit = null;
      this.editing = null;
      ((this as any).$refs.cardDlg as HTMLDialogElement | undefined)?.close();
    },

    // Commit: fire ONE whole-card cms-image-changed event on $root, keyed to this
    // card's slug, so the cmsEditor controller autosaves it into the card overlay.
    // Identity (slug/name) is NOT emitted — only the editable fields. image_ids is
    // EXCLUDED: the embedded image picker owns it (its own Save autosaves to the
    // separate property:card-images:<slug> target). Amenities are clamped to
    // MAX_AMENITIES as a final guard.
    confirmCard() {
      this._teardownHlSortable();
      const c = this.editing;
      this._preEdit = null;
      this.editing = null;
      ((this as any).$refs.cardDlg as HTMLDialogElement | undefined)?.close();
      if (!c) return;
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: {
            field: "card",
            target: `property:card:${c.slug}`,
            value: {
              teaser: c.teaser ?? "",
              featured_amenities: (c.featured_amenities ?? []).slice(0, MAX_AMENITIES),
              personas: c.personas ?? [],
            },
          },
          bubbles: true,
        }),
      );
    },

    // --- featured amenities repeater (max 3) ---

    addAmenity() {
      if (!this.editing) return;
      if (this.editing.featured_amenities.length >= MAX_AMENITIES) return;
      this.editing.featured_amenities = [
        ...this.editing.featured_amenities,
        { icon: "check", label: "" },
      ];
    },
    removeAmenity(idx: number) {
      this.editing?.featured_amenities.splice(idx, 1);
    },
    atMaxAmenities(): boolean {
      return !!this.editing && this.editing.featured_amenities.length >= MAX_AMENITIES;
    },

    // Icon picking is handled directly by the shared @IconTriggerButton in the
    // repeater row (its on-pick expr writes a.icon = name) — no controller method.

    // --- personas multi-select (fixed enum; toggled by the templ chips) ---

    togglePersona(key: string) {
      if (!this.editing) return;
      const set = new Set(this.editing.personas);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      this.editing.personas = [...set];
    },
    hasPersona(key: string): boolean {
      return !!this.editing && this.editing.personas.includes(key);
    },

    // --- reorder (immediate) ---

    // SortableJS on x-ref="cardList": reorders `cards` via the [data-card-handle]
    // grip and emits the new slug order on every drop. applySortableReorder reverts
    // Sortable's DOM move THEN reorders the array (so Alpine's keyed x-for doesn't
    // double-move the node), reading the new order from data-card-slug.
    _initSortable() {
      const el = (this as any).$refs.cardList as HTMLElement | undefined;
      if (!el) return;
      this._sortable = createReorderable(el, {
        handle: "[data-card-handle]",
        draggable: "[data-card]",
        onEnd: (e: Sortable.SortableEvent) => {
          this.cards = applySortableReorder(el, e, this.cards, "data-card-slug");
          this._emitOrder();
        },
      });
    },

    // SortableJS on the per-open x-ref="hlList": reorders editing.featured_amenities
    // via the [data-hl-handle] grip. Same revert-then-reorder helper as the card
    // list + the standalone repeater (applySortableReorder reads index, [data-hl-row]
    // just marks the rows). No event emit — the change rides the card's transactional
    // Done. Re-created each open (the modal body is rebuilt by x-if="editing").
    _initHlSortable() {
      this._teardownHlSortable();
      const el = (this as any).$refs.hlList as HTMLElement | undefined;
      if (!el) return;
      this._hlSortable = createReorderable(el, {
        handle: "[data-hl-handle]",
        draggable: "[data-hl-row]",
        onEnd: (e: Sortable.SortableEvent) => {
          if (!this.editing) return;
          this.editing.featured_amenities = applySortableReorder(
            el,
            e,
            this.editing.featured_amenities,
            "data-hl-row",
          );
        },
      });
    },
    _teardownHlSortable() {
      this._hlSortable?.destroy();
      this._hlSortable = null;
    },

    // Fire the slug-array order event on $root (immediate, no modal).
    _emitOrder() {
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: {
            field: "property_order",
            target: "site:property_order",
            value: this.cards.map((c) => c.slug),
          },
          bubbles: true,
        }),
      );
    },
  };
}
